import styled from 'styled-components';
import { DropdownMenuProps } from './dropdownMenu.types';

function getWidth(width: DropdownMenuProps['width']): string {
  switch (width) {
    case 'xs':
      return '51px';
    case 'sm':
      return '80px';
    case 'md':
      return '108px';
    case 'lg':
      return '134px';
    case 'xl':
      return '184px';
    case 'full':
      return '100%';
    default:
      return '108px';
  }
}

function getHeight(size: DropdownMenuProps['size']): string {
  switch (size) {
    case 'sm':
      return '28px';
    case 'lg':
      return '32px';
    default:
      return '32px';
  }
}

function getFontSize(size: DropdownMenuProps['size']): string {
  switch (size) {
    case 'sm':
      return 't4';
    case 'lg':
      return 't2';
    default:
      return 't2';
  }
}

export const Container = styled.button<{
  $isBlocked: boolean;
  $isDisabled: boolean;
  $isRequired: boolean;
  $open: boolean;
  $size: DropdownMenuProps['size'];
  $width: DropdownMenuProps['width'];
}>`
  display: flex;
  position: relative;
  height: ${({ $size }) => getHeight($size)};
  width: ${({ $width }) => getWidth($width)};
  padding: 8px;
  border-radius: ${({ theme: { radius }, $open }) =>
    $open ? `${radius.xs} ${radius.xs} 0 0` : radius.xs};
  box-shadow: ${({ theme: { shadows }, $open }) => ($open ? shadows.outerShadow : 'none')};

  transition: all 75ms;
  background-color: ${({ $isBlocked, theme: { color } }) =>
    $isBlocked ? 'transparent' : color.ui5};
  border: none;
  outline: ${({ $isRequired }) =>
    $isRequired
      ? '1px solid rgba(255, 70, 70, 1)' // We should be using color.t1 here but that doesn't exist yet
      : 'none'};
  cursor: pointer;
  filter: ${({ $isDisabled }) => `opacity(${$isDisabled ? 0.5 : 1})`};
  pointer-events: ${({ $isDisabled, $isBlocked }) => ($isDisabled || $isBlocked ? 'none' : 'auto')};
  z-index: ${({ $open }) => ($open ? 2 : 1)};

  // NOTE(omer): Upping the border width to 2px to prevent visual bug with 4k displays
  @media (-webkit-min-device-pixel-ratio: 1.5), (min-resolution: 144dpi) {
    outline-width: 2px;
  }
`;

export const OptionWrapper = styled.div<{ $isRequired: boolean; $show: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: ${({ $show }) => ($show ? 'auto' : '0')};
  background-color: ${({ theme: { color } }) => color.ui5};
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
  transform: ${({ $isRequired, $show }) =>
    `translateX(${$isRequired ? '-1px' : 0}) translateY(${$show ? 100 : 95}%)`};
  border-radius: ${({ theme: { radius } }) => `0 0 ${radius.xs} ${radius.xs}`};
  overflow: hidden;
  box-shadow: ${({ $isRequired, theme: { shadows } }) =>
    $isRequired ? 'none' : shadows.outerShadow};
  border: ${({ $isRequired }) => ($isRequired ? '1px solid rgba(255, 70, 70, 1)' : 'none')};
  border-top: none;
  z-index: 999;

  transition: all 75ms;

  // NOTE(omer): Upping the border width to 2px to prevent visual bug with 4k displays
  @media (-webkit-min-device-pixel-ratio: 1.5), (min-resolution: 144dpi) {
    border-width: 2px;
    transform: ${({ $isRequired, $show }) =>
      `translateX(${$isRequired ? '-2px' : 0}) translateY(${$show ? 100 : 95}%)`};
  }
`;

export const Option = styled.div<{ $size: DropdownMenuProps['size'] }>`
  text-align: left;
  padding: 8px;
  font-size: ${({ $size, theme: { font } }) => font[getFontSize($size)].fontSize};
  font-family: ${({ $size, theme: { font } }) => font[getFontSize($size)].fontFamily};
  font-weight: ${({ $size, theme: { font } }) => font[getFontSize($size)].fontWeight};
  color: ${({ theme: { color } }) => color.ui2};

  word-break: break-word;

  transition: all 75ms;
  &:hover {
    background-color: ${({ theme: { color } }) => color.s1};
    color: white;
  }
`;

export const SelectedOption = styled.div<{ $size: DropdownMenuProps['size'] }>`
  width: 100%;
  text-align: left;
  font-size: ${({ $size, theme: { font } }) => font[getFontSize($size)].fontSize};
  font-family: ${({ $size, theme: { font } }) => font[getFontSize($size)].fontFamily};
  font-weight: ${({ $size, theme: { font } }) => font[getFontSize($size)].fontWeight};
  color: ${({ theme: { color } }) => color.ui2};

  // truncate text
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChevronWrapper = styled.div<{ $open: boolean }>`
  height: 100%;
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  fill: ${({ theme: { color } }) => color.ui2};
  transform: ${({ $open }) => `rotate(${$open ? 180 : 0}deg)`};
  border-radius: ${({ theme: { radius } }) => radius.xs};
  background-color: ${({ theme: { color } }) => color.ui5};
`;
